import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuArrowLeft,
    LuPencil,
    LuTrash2,
    LuCheck,
    LuX,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import AssistenteComposer from '../components/AssistenteComposer';
import AssistenteMensagem from '../components/AssistenteMensagem';
import AssistentePensando from '../components/AssistentePensando';
import AssistenteService, {
    avisarConversasMudaram,
    type OrigemPergunta,
} from '../services/AssistenteService';
import { useConversaAssistente } from '../hooks/useConversaAssistente';
import './AssistenteLayout.css';

const AssistentePage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    const ehNova = id === 'novo';
    const conversaId = ehNova ? undefined : Number(id);

    /* A conversa em si (perguntar, avaliar, cartão de ação) é a mesma da Home:
       mora no hook. Aqui fica o que é desta tela — URL, título, exclusão. */
    const {
        conversa,
        setConversa,
        mensagens,
        setMensagens,
        perguntando,
        parcial,
        consulta,
        degradadas,
        perguntar: perguntarNaConversa,
        avaliar,
        trocarAcao,
    } = useConversaAssistente();
    const [carregando, setCarregando] = useState(!ehNova);

    const [editandoTitulo, setEditandoTitulo] = useState(false);
    const [tituloRascunho, setTituloRascunho] = useState('');
    const [confirmarExclusao, setConfirmarExclusao] = useState(false);
    const [excluindo, setExcluindo] = useState(false);

    const refFim = useRef<HTMLDivElement>(null);
    /* Qual conversa já está em memória. Sem isto, o `replace` da URL depois da
       primeira resposta dispararia um recarregamento que apagaria justamente o
       que acabou de chegar. */
    const refCarregada = useRef<number | null>(null);
    const refPerguntaInicial = useRef(false);

    /* O alcance não é mais escolha: é sempre o que o perfil do usuário alcança,
       decidido na API dentro de cada ferramenta. Por isso sumiu daqui e do
       subtítulo — ver AssistenteComposer. */

    /* ─── Carregar conversa existente ─────────────────────────── */
    useEffect(() => {
        if (ehNova || !conversaId || Number.isNaN(conversaId)) return;
        if (refCarregada.current === conversaId) return;

        let vivo = true;
        setCarregando(true);

        AssistenteService.buscarConversa(conversaId)
            .then(dados => {
                if (!vivo) return;
                refCarregada.current = conversaId;
                setConversa(dados.conversa);
                setMensagens(dados.mensagens);
            })
            .catch(() => {
                if (!vivo) return;
                aviso.erro('Conversa não encontrada.');
                navigate('/home', { replace: true });
            })
            .finally(() => { if (vivo) setCarregando(false); });

        return () => { vivo = false; };
    }, [conversaId, ehNova, navigate, setConversa, setMensagens]);

    /* ─── Perguntar ───────────────────────────────────────────── */
    const perguntar = useCallback(async (texto: string, idExistente?: number, origem: OrigemPergunta = 'digitada') => {
        const dados = await perguntarNaConversa(texto, idExistente, origem);
        if (!dados) return;

        refCarregada.current = dados.conversa.conversaId;

        /* A conversa nova ganha URL própria sem recarregar nada: o
           `refCarregada` acima já marcou que ela está em memória. */
        if (!idExistente) {
            navigate(`/assistente/${dados.conversa.conversaId}`, { replace: true });
        }
    }, [navigate, perguntarNaConversa]);

    /* A pergunta que veio da Home dispara uma vez só. */
    useEffect(() => {
        if (!ehNova || refPerguntaInicial.current) return;

        const inicial = (location.state as any)?.pergunta as string | undefined;
        const origem = (location.state as any)?.origem === 'sugestao' ? 'sugestao' : 'digitada';
        if (!inicial) {
            navigate('/home', { replace: true });
            return;
        }

        refPerguntaInicial.current = true;
        perguntar(inicial, undefined, origem);
    }, [ehNova, location.state, navigate, perguntar]);

    /* Rola para o fim a cada mensagem — inclusive enquanto o "digitando" aparece. */
    useEffect(() => {
        refFim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [mensagens.length, perguntando, parcial]);

    /* ─── Renomear e excluir ──────────────────────────────────── */
    const abrirEdicaoTitulo = () => {
        if (!conversa) return;
        setTituloRascunho(conversa.titulo);
        setEditandoTitulo(true);
    };

    const salvarTitulo = async () => {
        if (!conversa) return;
        const titulo = tituloRascunho.trim();
        if (!titulo || titulo === conversa.titulo) { setEditandoTitulo(false); return; }

        try {
            const atualizada = await AssistenteService.renomear(conversa.conversaId, titulo);
            setConversa(atualizada);
            avisarConversasMudaram();
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível renomear.'));
        } finally {
            setEditandoTitulo(false);
        }
    };

    const excluir = async () => {
        if (!conversa) return;
        setExcluindo(true);
        try {
            await AssistenteService.remover(conversa.conversaId);
            avisarConversasMudaram();
            aviso.ok('Conversa removida.');
            navigate('/home', { replace: true });
        } catch (erro) {
            aviso.erro(textoDoErro(erro, 'Não foi possível remover.'));
        } finally {
            setExcluindo(false);
            setConfirmarExclusao(false);
        }
    };

    const subtitulo = conversa
        ? new Date(conversa.atualizadaEm).toLocaleDateString('pt-BR')
        : '';

    return (
        <div className="page-shell as-screen as-conversa">
            <PageHeader
                centered
                leading={
                    <button
                        type="button"
                        className="as-voltar"
                        onClick={() => navigate('/home')}
                        aria-label="Voltar para a Home"
                        title="Voltar"
                    >
                        <LuArrowLeft size={16} />
                    </button>
                }
                title={editandoTitulo ? '' : (conversa?.titulo ?? (carregando ? 'Carregando…' : 'Nova conversa'))}
                subtitle={subtitulo}
                actions={
                    conversa && !editandoTitulo ? (
                        <>
                            <button type="button" className="as-acao" onClick={abrirEdicaoTitulo}>
                                <LuPencil size={14} /> Renomear
                            </button>
                            <button
                                type="button"
                                className="as-acao is-danger"
                                onClick={() => setConfirmarExclusao(true)}
                                aria-label="Excluir conversa"
                                title="Excluir conversa"
                            >
                                <LuTrash2 size={14} />
                            </button>
                        </>
                    ) : undefined
                }
            />

            <div className="page-body as-fill">
                {/* A área que rola ocupa a largura toda (a barra fica no canto da
                    tela); a coluna de leitura vai centralizada dentro dela. */}
                <div className="as-rolagem">
                    <div className="as-coluna">
                        {editandoTitulo && (
                            <div className="as-bloco-h" style={{ marginBottom: 'var(--space-4)' }}>
                                <input
                                    className="as-titulo-input"
                                    value={tituloRascunho}
                                    maxLength={60}
                                    autoFocus
                                    onChange={e => setTituloRascunho(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') salvarTitulo();
                                        if (e.key === 'Escape') setEditandoTitulo(false);
                                    }}
                                />
                                <button type="button" className="as-ic" onClick={salvarTitulo} aria-label="Salvar título">
                                    <LuCheck size={16} />
                                </button>
                                <button type="button" className="as-ic" onClick={() => setEditandoTitulo(false)} aria-label="Cancelar">
                                    <LuX size={16} />
                                </button>
                            </div>
                        )}

                        <div className="as-conv">
                            {mensagens.map((m, i) => (
                                <AssistenteMensagem
                                    key={m.mensagemId}
                                    mensagem={m}
                                    degradada={degradadas.has(m.mensagemId)}
                                    onAvaliar={avaliar}
                                    onAcaoMudou={trocarAcao}
                                    /* A última resposta do modelo: é só nela que o Brik AI pergunta se ajudou. */
                                    ultima={!perguntando && m.papel === 'model' && !mensagens.slice(i + 1).some(o => o.papel === 'model')}
                                />
                            ))}

                            {/* A resposta aparecendo enquanto é escrita; a gravada a substitui no fim. */}
                            {perguntando && parcial && (
                                <AssistenteMensagem
                                    mensagem={{ mensagemId: -1, papel: 'model', conteudo: parcial, fontes: [], criadaEm: new Date().toISOString() }}
                                />
                            )}

                            {perguntando && !parcial && (
                                <div className="as-msg is-model">
                                    <AssistentePensando consulta={consulta} />
                                </div>
                            )}

                            <div ref={refFim} />
                        </div>
                    </div>
                </div>

                <div className="as-coluna as-doca">
                    <AssistenteComposer
                        onEnviar={texto => perguntar(texto, conversa?.conversaId)}
                        enviando={perguntando}
                        autoFocus={!ehNova}
                    />
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmarExclusao}
                onClose={() => setConfirmarExclusao(false)}
                onConfirm={excluir}
                title="Excluir conversa"
                message="A conversa sai da sua lista e não dá para recuperar. As telas e os dados da plataforma não mudam."
                confirmText="Excluir"
                isConfirming={excluindo}
            />
        </div>
    );
};

export default AssistentePage;
